import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, FileText, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { trackPurchase, trackRegistrationSuccess } from "../lib/tracking";
import { clearStoredReferralCode } from "../lib/referral";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useSettings } from "../lib/settings";
import { getPaymentBranding } from "../lib/paymentBranding";
import { isFped } from "../lib/regime";

export default function Success() {
  const [params] = useSearchParams();
  const [purchase, setPurchase] = useState(null);
  const [enrollment, setEnrollment] = useState(null);
  const purchaseId = params.get("purchase_id");
  const enrollmentId = params.get("enrollment_id");
  const queryMode = params.get("mode"); // hint from URL — "registration" | "paid"
  const settings = useSettings();

  useEffect(() => {
    // Dedupe conversion events across refreshes/navigations. Each unique id
    // (purchase id or registration enrollment id) fires its analytics event
    // exactly once per browser. Persisted in localStorage with fallback to
    // sessionStorage when localStorage is unavailable (private mode, ITP).
    const fireOnce = (key, fn) => {
      const storageKey = `_evt_${key}`;
      let already = false;
      try { already = localStorage.getItem(storageKey) === "1"; } catch (_) {
        try { already = sessionStorage.getItem(storageKey) === "1"; } catch (_) { /* storage unavailable */ }
      }
      if (already) return;
      try { fn(); } finally {
        try { localStorage.setItem(storageKey, "1"); } catch (_) {
          try { sessionStorage.setItem(storageKey, "1"); } catch (_) { /* storage unavailable */ }
        }
      }
    };

    if (purchaseId) {
      api.get(`/payment/status/${purchaseId}`).then((r) => {
        setPurchase(r.data);
        if (r.data.enrollment) setEnrollment(r.data.enrollment);
        if (r.data.mode === "registration_only") {
          fireOnce(`reg_${r.data.id}`, () => trackRegistrationSuccess(r.data.id));
        } else if (r.data.status === "paid") {
          // Only ever emit `purchase` for a REAL paid transaction.
          fireOnce(`purchase_${r.data.id}`, () => trackPurchase(r.data));
        }
      }).catch(() => {});
    } else if (enrollmentId) {
      api.get(`/enrollments/status/${enrollmentId}`).then((r) => {
        setEnrollment(r.data);
        if (r.data.payment) setPurchase(r.data.payment);
        if (r.data.enrollment_mode === "registration_only") {
          fireOnce(`reg_${r.data.id}`, () => trackRegistrationSuccess(r.data.id));
        } else if (r.data.payment?.status === "paid") {
          fireOnce(`purchase_${r.data.payment.id}`, () => trackPurchase(r.data.payment));
        }
      }).catch(() => {});
    }
    // The participant has reached /success — drop any stored ?ref=CODE so the
    // next visit starts clean (avoids re-applying a code on a different lead).
    clearStoredReferralCode();
  }, [purchaseId, enrollmentId]);

  // Determine variant: prefer the loaded enrollment, fall back to purchase or URL hint.
  const isRegistration = enrollment
    ? enrollment.enrollment_mode === "registration_only"
    : (purchase ? purchase.mode === "registration_only" : queryMode === "registration");
  const isPaid = purchase ? purchase.status === "paid" : queryMode === "paid";
  const isPending = purchase && purchase.status === "pending"; // Netopia hasn't called IPN yet

  let title, subtitle, statusBadge;
  // Through a cohort's own link, paid by an institution (07.10.2026): nothing
  // to pay, ever — the default registration wording would promise payment details.
  if (enrollment?.billing_mode === "institution") {
    title = "Înscrierea ta a fost primită.";
    subtitle = `Taxa de participare este acoperită de ${enrollment.institution_name || "instituția ta"}. `
      + "Echipa FPED îți confirmă înscrierea, apoi primești pe email linkul pentru datele cerute de minister "
      + "și contractul de semnat.";
    statusBadge = "Înscriere trimisă";
  } else if (isRegistration) {
    title = "Înscrierea ta a fost înregistrată.";
    subtitle = settings.success_text_registration
      || "Echipa noastră va reveni către tine cu detaliile referitoare la plată și pașii următori.";
    statusBadge = "Înscriere trimisă";
  } else if (isPaid) {
    title = "Felicitări! Plata a fost confirmată.";
    subtitle = settings.success_text_paid
      || "Ți-am trimis pe email confirmarea plății, factura și instrucțiunile de acces.";
    statusBadge = "Plată confirmată";
  } else if (isPending) {
    // M901 — use the provider stored on THIS purchase (not the currently
    // active one). The user might land on Success.jsx after the admin
    // already switched the provider for new purchases.
    const purchaseBranding = getPaymentBranding(purchase?.provider);
    title = "Procesăm plata ta…";
    subtitle = purchaseBranding.awaitingConfirmationText;
    statusBadge = "În procesare";
  } else {
    title = "Mulțumim!";
    subtitle = "Vei primi un email cu detaliile.";
    statusBadge = "În procesare";
  }

  // M913B — Container is NOT masked: titles, subtitles, status badges and
  // CTAs stay visible in Clarity. Only the actual transaction VALUE shown
  // in the `<Field value>` slot below (#order_id, amount) gets masked.
  return (
    <div className="min-h-screen bg-[#F5F6F6]">
      <Header />
      <main className="max-w-3xl mx-auto px-6 lg:px-8 py-20" data-testid="success-page">
        <div className="bg-white rounded-3xl border border-[#E5E7EB] p-8 sm:p-12">
          <header className="text-center">
            <div className={`h-16 w-16 mx-auto rounded-full grid place-items-center text-white ${isRegistration ? "bg-[#965EA7]" : "bg-[#519872]"}`}>
              {isRegistration ? <FileText size={32} /> : <CheckCircle2 size={32} />}
            </div>
            <h1 className="mt-6 font-display text-3xl md:text-4xl font-bold text-[#373A45]" data-testid="success-title">
              {title}
            </h1>
            <p className="mt-3 text-[#6B7280]" data-testid="success-subtitle">{subtitle}</p>
            {/* A Curs FPED enrolment is outside the accreditation (12.10.2026). */}
            {!isFped(enrollment) && (
              <p className="mt-2 text-xs text-[#6B7280] max-w-md mx-auto" data-testid="success-accreditation">
                Program acreditat prin OMEC nr. 4712/19.08.2026.
              </p>
            )}
          </header>

          {purchase && (
            <section
              className="mt-8 sm:mt-10 pt-8 sm:pt-10 border-t border-[#E5E7EB]"
              data-testid="success-info-section"
              aria-label={isRegistration ? "Detalii înscriere" : "Detalii comandă"}
            >
              <p className="text-center text-xs uppercase tracking-widest text-[#6B7280] font-semibold">
                {isRegistration ? "Detalii înscriere" : "Detalii comandă"}
              </p>
              <div
                className="mt-4 mx-auto max-w-md rounded-2xl bg-[#EDEEF0] border border-[#E5E7EB] px-5 py-4"
                data-testid="success-summary"
              >
                <dl className="grid grid-cols-3 gap-3 text-center">
                  <Field label={isRegistration ? "Înscriere" : "Comandă"} value={`#${purchase.id?.slice(0, 8)}`} mono />
                  {!isRegistration && (
                    <Field
                      label="Sumă"
                      value={`${Number(purchase.amount).toLocaleString("ro-RO")} ${purchase.currency}`}
                    />
                  )}
                  {isRegistration && (
                    <Field label="Plată" value="Fără plată" />
                  )}
                  <Field label="Status" value={statusBadge} />
                </dl>
              </div>
            </section>
          )}

          <section className="mt-8 sm:mt-10 flex justify-center" data-testid="success-action-section">
            <Link
              to="/"
              className="w-full sm:w-auto sm:min-w-[280px] inline-flex items-center justify-center gap-2 rounded-full bg-[#965EA7] hover:bg-[#7A4986] text-white font-semibold px-7 py-4 shadow-[0_4px_14px_0_rgba(150,94,167,0.39)] hover:-translate-y-0.5 transition-all"
              data-testid="success-home-cta"
            >
              Înapoi la pagina principală <ArrowRight size={18} />
            </Link>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function Field({ label, value, mono = false }) {
  return (
    <div>
      <dt className="text-[10px] uppercase tracking-widest text-[#6B7280] font-semibold">
        {label}
      </dt>
      <dd
        className={`mt-1 text-sm font-semibold text-[#373A45] clarity-mask ${mono ? "font-mono" : ""}`}
        data-clarity-mask="true"
      >
        {value}
      </dd>
    </div>
  );
}
